/* eslint-disable max-lines */

import { A } from '@adornis/base/env-info.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { subscribeAllEntities } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-loader.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import '@adornis/datetime/x-date-picker-dropdown.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@adornis/popover/x-tooltip.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { emailRegexCheck } from '@adornis/users/db/validators.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { FormOfFinancing } from '@chancenwerk/_cms/financing/entities/FormOfFinancing.js';
import { ProductLKK } from '@chancenwerk/_cms/products/entities/ProductLKK.js';
import { RegisterEventlog, RegisterEventlogType } from '@chancenwerk/db/RegisterEventlog.js';
import { Adresse, ZipCity } from '@chancenwerk/db/adresse.js';
import { CourseDigital } from '@chancenwerk/db/courses/CourseDigital.js';
import { CourseIntensive } from '@chancenwerk/db/courses/CourseIntensive.js';
import { CourseIntensiveSingle } from '@chancenwerk/db/courses/CourseIntensiveSingle.js';
import { Mitgliedschaften, PaymentMethod, StudentType } from '@chancenwerk/db/enums/enums.js';
import { AnmeldungConversionEventlog } from '@chancenwerk/db/eventlogs/AnmeldungConversionEventlog.js';
import { Role } from '@chancenwerk/db/semantics/Role.js';
import { SemanticRelationship } from '@chancenwerk/db/semantics/SemanticRelationship.js';
import { StudentParticipationRelationship } from '@chancenwerk/db/semantics/StudentParticipationRelationship.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import '@chancenwerk/design/components/cr-date-picker-dropdown.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-multiselection.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import { type ChromiumFlex } from '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-grid.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-image.js';
import '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/cr-loader.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/cr-toggle-button.js';
import '@chancenwerk/design/components/fonts.js';
import { FormOfFinancingLogicExpression } from '@chancenwerk/master-data-management/kpf/entities/FormOfFinancingLogicExpression.js';
import { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import type { School } from '@chancenwerk/master-data-management/partner/entities/School.js';
import { SchoolInformation, Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { KPFPublicInformationAPI } from '@chancenwerk/public-access/api/KPFPublicInformationAPI.js';
import { PartnerPublicInformationAPI } from '@chancenwerk/public-access/api/PartnerPublicInformationAPI.js';
import { UserPublicInformationAPI } from '@chancenwerk/public-access/api/UserPublicInformationAPI.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { DateTime } from 'luxon';
import { combineLatest, filter, from, switchMap, takeUntil, tap } from 'rxjs';
import { KPF } from '../../../../src/master-data-management/kpf/entities/KPF.js';
import '../../../../src/print-components/chancen-component-anmeldung-consent.js';
import '../components/consent-texts/anmeldung-component-formfield-consent-eval.js';
import '../components/consent-texts/anmeldung-component-formfield-consent-picture.js';
import '../cr-cta-text.js';
import './anmeldung-component-footer.js';
import './anmeldung-component-formular-attendance.js';
import { AnmeldungPageRegistrationBase } from './anmeldung-component-formular-base.js';
import './anmeldung-component-formular-but.js';
import './anmeldung-component-formular-data-overview.js';
import './anmeldung-component-formular-data-validation.js';
import './anmeldung-component-formular-digital-courses.js';
import './anmeldung-component-formular-finished.js';
import './anmeldung-component-formular-lang-select.js';
import './anmeldung-component-formular-lkk-intensive.js';
import './anmeldung-component-formular-lkk-us.js';
import './anmeldung-component-formular-lkk.js';
import './anmeldung-component-formular-personal-info.js';
import './anmeldung-component-formular-school-info.js';
import './anmeldung-component-formular-support.js';

/**
 * Defines the starting step of the formular. This is only applied in a dev environment.
 */
const DEVELOPMENT_STARTING_STEP = 1;

/**
 * Will insert some basic personal info into the student for faster access to later pages.
 */
const DEVELOPMENT_INSERT_MOCK_DATA = true;

const formOfFionanzingSelector: BaseQLSelectionSet<FormOfFinancingLogicExpression | FormOfFinancing> = {
  ...FormOfFinancingLogicExpression.allFields,
  ...FormOfFinancing.allFields,
};

const kpfSelector: BaseQLSelectionSet<KPF> = {
  _class: 1,
  _id: 1,
  endDatum: 1,
  email: 1,
  produkt: {
    // @ts-ignore Only exists on a special product
    durchfuehrungsTage: {
      _class: 1,
      wochentag: 1,
      startZeit: 1,
      endZeit: 1,
      locations: 1,
      cid: 1,
      info: 1,
    },
    kuerzel: 1,
    _class: 1,
  },
  schoolID: 1,
  school: {
    _id: 1,
    _class: 1,
    name: 1,
    kuerzel: 1,
    address: Adresse.allFields,
  },
  standardMembershipFee: 1,
  formOfFinanzingLogicString: formOfFionanzingSelector,
};

const courseSelectorDigital: BaseQLSelectionSet<CourseDigital> = {
  _class: 1,
  _id: 1,
  weekday: 1,
  startTime: 1,
  endTime: 1,
  linkedKPFIDs: 1,
  linkedKPFs: 1,
  location: 1,
  osStudentIDs: 1,
  jahrgangsstufe: 1,
  subject: 1,
};

const courseSelectorIntensive: BaseQLSelectionSet<CourseIntensive> = {
  _class: 1,
  _id: 1,
  weekday: 1,
  startTime: 1,
  endTime: 1,
  location: 1,
  osStudentIDs: 1,
  subjectOne: 1,
  subjectTwo: 1,
};

interface FormularStep {
  title: string;
  key?: string;
  render: () => Renderable;
  process?: () => Promise<void>;
  show?: () => boolean;
}

/**
 * @customElement anmeldung-component-formular
 */
@customElement('anmeldung-component-formular')
export class AnmeldungPageRegistration extends AnmeldungPageRegistrationBase {
  @property({ type: Number, attribute: false }) step = new RXController(
    this,
    A.isDevelopment ? DEVELOPMENT_STARTING_STEP : 1,
  ); // ! Dont change the step here, use the const at the start
  @property({ attribute: 'school-level' }) schoolLevel = new RXController<'us' | 'os'>(this, 'us');

  // Used for conversion tracking
  @state() private readonly _uniqueID = A.getGloballyUniqueID();

  @state() private _abwesenheitAgreed = false;

  @state() private readonly _student = new RXController(
    this,
    new Student({
      username: 'new',
      schuelertyp: this.schoolLevel.value === 'os' ? StudentType.OS : StudentType.US,
      //@ts-expect-error Date is not DateTime
      schoolInformation: new SchoolInformation({
        jahrgangsstufe: null,
      }),
    }),
  );

  @state() private readonly _kpfs = new RXController(
    this,
    from(KPFPublicInformationAPI.getAll()(kpfSelector)).pipe(
      switchMap(async kpfs => {
        kpfs = kpfs.filter(kpf => {
          if (kpf.produkt?._class !== ProductLKK._class) return false;
          if (!kpf.endDatum) return true;
          return kpf.endDatum.toMillis() >= DateTime.now().toMillis();
        });

        const partners = await PartnerPublicInformationAPI.getAll()({
          name: 1,
          address: Adresse.allFields,
        });

        for (const kpf of kpfs) {
          const partner = partners.find(p => p?._id === kpf.schoolID);
          if (!partner) {
            continue;
          }

          kpf.school = constructValue(partner as Partner);
        }

        return kpfs;
      }),
    ),
  );

  @state() private readonly _choosenKPF = new RXController<KPF | undefined>(this, undefined);

  @state() private readonly translation = new TranslationController(this);

  @state() private readonly _schoolCityForFilter = new RXController<ZipCity | undefined>(this, new ZipCity({}));

  @state() private readonly _size = new WindowSizeController(this);

  @state() private readonly _subjects = new RXController<string[]>(this, ['', '', '']);

  @state() private readonly _intensiveCourses = new RXController(
    this,
    this._choosenKPF.observable.pipe(
      switchMap(kpf =>
        CourseIntensive.subscribeCoursesByKPFID([kpf?._id ?? ''], CourseIntensive._class)(courseSelectorIntensive),
      ),
    ),
  );

  @state() private readonly _digitalCoursesChoosen: CourseDigital[] = [];

  @state() private readonly _digitalCourses = new RXController(
    this,
    combineLatest([this._student.observable, this._subjects.observable]).pipe(
      switchMap(([student, subjects]) =>
        CourseDigital.subscribeDigitialCourseSubjectAndGrade(
          subjects,
          student.schoolInformation.jahrgangsstufe ?? 0,
        )(courseSelectorDigital),
      ),
    ),
  );

  @state() private readonly _intensiveCoursesSingle = new RXController(
    this,
    combineLatest([this._student.observable, this._subjects.observable]).pipe(
      switchMap(([student, subjects]) =>
        subscribeAllEntities(CourseIntensiveSingle)({
          name: 1,
          subject: 1,
          startTime: 1,
          endTime: 1,
          weekday: 1,
          location: 1,
        }),
      ),
    ),
  );

  private readonly _legalGuardian1AddressDifferent = new RXController(this, false);
  private readonly _legalGuardian2AddressDifferent = new RXController(this, false);
  private readonly _formHeadlineEl = createRef<ChromiumFlex>();

  private readonly _oldSchoolCityForFilter: ZipCity | undefined = undefined;

  private readonly _formularSteps: FormularStep[] = [
    {
      title: 'Sprache auswählen / Choose your preferred language',
      render: this._getLanguageSelection.bind(this),
      show: () => false,
    },

    {
      title: this.translation.translate('step1', 'header'),
      render: this._getSchoolInformationSection.bind(this),
      process: () => this._processStepSchoolSelect(this._student.value),
    },

    {
      title: 'Persönliche Daten',
      key: 'personalData',
      render: this._getPersonalInformationSection.bind(this),
      process: () => this._processStepPersonalInfo(this._student.value),
    },

    {
      title: this.translation.translate('moreInformationForChild'),
      render: this._getSupportSection.bind(this),
      show: () => this.schoolLevel.value === 'us',
    },

    {
      title: 'Lernförderung und Intensivkursangebote',
      render: this._getLKKSection.bind(this),
      show: () => this.schoolLevel.value === 'os',
    },

    {
      title: 'Intensivkursangebote für dich',
      render: this._getLKKSectionOfferHelp.bind(this),
      show: () =>
        this.schoolLevel.value === 'os' && this._student.value.mitgliedschaft === Mitgliedschaften.HILFE_GEBEN_NEHMEN,
    },

    {
      title: 'Angebot 1',
      render: this._getLKKSectionOffer1.bind(this),
      show: () =>
        this.schoolLevel.value === 'os' && this._student.value.mitgliedschaft === Mitgliedschaften.HILFE_GEBEN_NEHMEN,
    },

    {
      title: 'Angebot 2',
      render: this._getLKKSectionOffer2.bind(this),
      show: () =>
        this.schoolLevel.value === 'os' && this._student.value.mitgliedschaft === Mitgliedschaften.HILFE_GEBEN_NEHMEN,
    },

    {
      title: 'Angebot 3',
      render: this._getLKKSectionOffer3.bind(this),
      show: () =>
        this.schoolLevel.value === 'os' &&
        this._student.value.mitgliedschaft === Mitgliedschaften.HILFE_GEBEN_NEHMEN &&
        this._student.value.schoolInformation.jahrgangsstufe === 10,
    },

    {
      title: 'Mitgliedsbeitrag und SEPA-Lastschriftmandat',
      render: this._getBUTSection.bind(this),
      key: 'but',
      process: () => this._processStepBuT(this._student.value),
      show: () => this.schoolLevel.value === 'us',
    },

    // {
    //   title: 'Abmeldung',
    //   render: this._abmeldungBeiAbwesenheit.bind(this),
    //   process: () => this._processStepAbwesenheit(this._student.value),
    //   show: () => this.schoolLevel.value === 'us',
    // },

    // US finishes after this
    {
      title: 'Datenübersicht',
      render: this._getDataValidation.bind(this),
      process: () => this._processStepFinish(this._student.value),
      show: () => this.schoolLevel.value === 'us',
    },

    {
      title: 'Datenübersicht',
      render: this._getDataValidation.bind(this),
      show: () => this.schoolLevel.value === 'os',
    },

    // OS finishes after this
    {
      title: 'Einwilligungserklärungen',
      render: this._getDataOverview.bind(this),
      process: () => this._processStepFinish(this._student.value),
      show: () => this.schoolLevel.value === 'os',
    },

    {
      title: '',
      render: this._getFinishedScreen.bind(this),
    },
  ];

  @state() private readonly _form = new FormController(this, this._student.observable, {
    onSuccess: async () => {
      const student = this._student.value;

      student.trimWhitespace();

      await AnmeldungConversionEventlog.createOrUpdateAnmeldungConversionEventlog(
        this._uniqueID,
        this.schoolLevel.value,
        this.step.value,
        false,
        this._formularSteps[this.step.value]?.title ?? '',
      )();

      await this._formularSteps[this.step.value]?.process?.();
      this._nextUp();

      if (this.schoolLevel.value === 'us') {
        this._student.value.bankInformation.zahlungsart = PaymentMethod.DIGITAL_NOT_FINISHED;
      }
      this._student.next(student);
    },
    success: () => {
      requestAnimationFrame(() =>
        this._formHeadlineEl.value?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' }),
      );
    },
    error: err => {
      if (err instanceof MultipleValidationError)
        return ChromiumSnackbar.show(err.extensions.validationErrors?.[0]?.message ?? err.message);
      return ChromiumSnackbar.show(err.message);
    },
    validationMeta: () => ({
      step: this.step.value,
      stepKey: this._formularSteps[this.step.value]?.key,
    }),
  });

  private async _processStepSchoolSelect(student: Student) {
    if (!this._choosenKPF.value) {
      const errMsg = 'Eine Schule muss ausgewählt sein!';
      void ChromiumSnackbar.show(errMsg);
      throw new ValidationError(errMsg);
    }

    if (!student.schoolInformation.jahrgangsstufe) {
      const errMsg = 'Eine Jahrgangsstufe muss ausgewählt sein!';
      void ChromiumSnackbar.show(errMsg);
      throw new ValidationError(errMsg, {
        path: ['schoolInformation'],
        key: 'jahrgangsstufe',
        translationKey: 'errorJahrgangsstufe',
        source: student,
      });
    }
  }

  // eslint-disable-next-line complexity
  private async _processStepPersonalInfo(student: Student) {
    if (this.schoolLevel.value === 'os' && !student.email) {
      throw new ValidationError(`Bitte gebe deine E-Mail an!`, {
        key: 'email',
        source: student,
        translationKey: 'errorEmail',
      });
    }

    if (this.schoolLevel.value === 'os' && !student.handynummer) {
      throw new ValidationError(`Bitte gebe deine Handynummer an!`, {
        key: 'handynummer',
        source: student,
        translationKey: 'errorHandynummer',
      });
    }

    if (this.schoolLevel.value === 'os' && !student.geburtstag) {
      throw new ValidationError(`Bitte gebe deinen Geburtstag an!`, {
        key: 'geburtstag',
        source: student,
        translationKey: 'errorGeburtstagOs',
      });
    } else if (this.schoolLevel.value === 'us' && !student.geburtstag) {
      throw new ValidationError(`Bitte geben Sie einen Geburtstag an!`, {
        key: 'geburtstag',
        source: student,
        translationKey: 'errorGeburtstagUs',
      });
    }

    if (!student.adresse?.strasse) {
      throw new ValidationError(`Die Adresse muss vollständig angegeben werden! Straße fehlt...`, {
        path: ['adresse'],
        key: 'strasse',
        source: student,
        translationKey: 'errorStrasse',
      });
    }

    if (!/\d/.test(student.adresse.strasse)) {
      if (
        !(await ChromiumDialog.confirm(
          `Ihre Straße '${student.adresse.strasse}' enthält keine Hausnummer. Ist das wirklich korrekt?`,
        ))
      ) {
        throw new ValidationError(`Die Adresse muss vollständig angegeben werden!`, {
          path: ['adresse'],
          key: 'strasse',
          source: student,
          translationKey: 'errorStrasse',
        });
      }
    }

    if (!student.adresse?.zipCity?.zip?.trim().length || !student.adresse.zipCity.city?.trim().length) {
      throw new ValidationError(`Die Adresse muss vollständig angegeben werden! Stadt fehlt...`, {
        path: ['adresse'],
        key: 'zipCity',
        source: student,
        translationKey: 'errorStadt',
      });
    }

    if (!student.legalGuardian1.vorname) {
      throw new ValidationError(`Bitte geben Sie einen Vornamen an`, {
        path: ['legalGuardian1'],
        key: 'vorname',
        source: student,
        translationKey: 'errorVorname',
      });
    }
    if (!student.legalGuardian1.nachname) {
      throw new ValidationError(`Bitte geben Sie einen Nachname an`, {
        path: ['legalGuardian1'],
        key: 'nachname',
        source: student,
        translationKey: 'errorNachname',
      });
    }

    if (!student.legalGuardian1.handynummer && !student.legalGuardian1.festnetznummer) {
      const errMsg = 'Die Handy- oder Festnetznummer eines Erziehungsberechtigten wird benötigt';

      void ChromiumSnackbar.show(errMsg);
      throw new ValidationError(errMsg, {
        path: ['legalGuardian1'],
        key: 'handynummer',
        source: student,
        translationKey: 'errorHandyFestnetz',
      });
    }

    if (this.schoolLevel.value === 'us' && !student.legalGuardian1.email && !student.legalGuardian2.email) {
      const errMsg = 'Die E-Mail-Adresse eines Erziehungsberechtigten wird benötigt';

      void ChromiumSnackbar.show(errMsg);
      throw new ValidationError(errMsg, {
        path: ['legalGuardian1'],
        key: `email`,
        source: this._student.value,
        translationKey: 'errorEmailGuardian',
      });
    } else {
      if (student.legalGuardian1.email) {
        if (!emailRegexCheck(student.legalGuardian1.email))
          throw new ValidationError(`${student.legalGuardian1.email} ist keine gültige E-Mail.`, {
            path: ['legalGuardian1'],
            key: 'email',
            source: student,
            translationKey: 'errorEmailInvalid',
          });
      }

      if (student.legalGuardian2.email) {
        if (!emailRegexCheck(student.legalGuardian2.email))
          throw new ValidationError(`${student.legalGuardian2.email} ist keine gültige E-Mail.`, {
            path: ['legalGuardian2'],
            key: 'email',
            source: student,
            translationKey: 'errorEmailInvalid',
          });
      }
    }
    if (student.legalGuardian1.email) this._student.value.email = this._student.value.legalGuardian1.email;
    else if (student.legalGuardian2.email) this._student.value.email = this._student.value.legalGuardian2.email;

    if (this.schoolLevel.value === 'us') {
      if (
        await UserPublicInformationAPI.anmeldungAlreadyRegistered([
          student.email,
          student.legalGuardian1.email,
          student.legalGuardian2.email,
        ])()
      ) {
        if (
          !(await ChromiumDialog.confirm(
            'Die angegebene E-Mail-Adresse ist bereits in unserem System. Wollen sie trotzdem fortfahren? Für Fragen melden sie sich gerne bei verwaltung@chancenwerk.org',
          ))
        ) {
          throw new ValidationError('Bitte melden Sie sich bei der Verwaltung');
        }
      }
    }

    await this._addressConfirm(student);
  }

  private async _addressConfirm(student: Student) {
    const addressConfirm = html`
      <cr-flex space="sm" padding="md">
        <cr-h3>${this.translation.translate('address_confirm')}</cr-h3>

        <cr-grid columns="auto auto" space="md" padding="md">
          <cr-h4> Anschrift: </cr-h4>
          <cr-text> ${student.adresse?.strasse} </cr-text>

          <cr-h4> Stadt: </cr-h4>
          <cr-text> ${student.adresse?.zipCity?.zip}, ${student.adresse?.zipCity?.city} </cr-text>

          <cr-h4> E-Mail: </cr-h4>
          <cr-text>
            ${student.email.length === 0 || student.legalGuardian1.email || student.legalGuardian2.email}
          </cr-text>
        </cr-grid>
      </cr-flex>
    `;
    const emailConfirm = html`
      <cr-flex space="sm" padding="md">
        <cr-h3>Stimmen deine angegebenen Kontaktdaten?</cr-h3>

        <cr-grid columns="auto auto" space="md" padding="md">
          <cr-h4> E-Mail: </cr-h4>
          <cr-text> ${student.email} </cr-text>

          <cr-h4> Handynummer: </cr-h4>
          <cr-text> ${student.handynummer} </cr-text>
        </cr-grid>
      </cr-flex>
    `;
    if (!(await XDialog.confirm(this.schoolLevel.value === 'os' ? emailConfirm : addressConfirm)))
      throw new ValidationError('Adresse wurde als nicht korrekt gekennzeichnet!', {
        path: ['legalGuardian1'],
        key: 'adresse',
        source: this._student.value,
        translationKey: 'errorAddressIncorect',
      });
  }

  private async _processStepBuT(student: Student) {
    if (this.schoolLevel.value === 'us' && !student.buts[0]?.BuTBerechtigung) {
      if (!student.bankInformation.kontoinhaber) {
        const errMsg = 'Kontoinhaber:in muss ausgefüllt werden, wenn keine BuT Berechtigung vorliegt';

        void ChromiumSnackbar.show(errMsg);
        throw new ValidationError(errMsg, {
          path: ['bankInformation'],
          key: 'kontoinhaber',
          source: this._student.value,
          translationKey: 'errorBuT',
        });
      }
      if (!student.bankInformation.iban) {
        const errMsg = 'Die IBAN muss ausgefüllt werden, wenn keine BuT Berechtigung vorliegt';

        void ChromiumSnackbar.show(errMsg);
        throw new ValidationError(errMsg, {
          path: ['bankInformation'],
          key: 'iban',
          source: this._student.value,
          translationKey: 'errorBuTIBAN',
        });
      }
    }

    if (this.schoolLevel.value === 'us' && student.buts[0]?.BuTBerechtigung && !student.buts[0]?.BuTBerechtigungDurch) {
      const errMsg = '"BuT-Berechtigt durch" muss ausgefüllt werden';

      void ChromiumSnackbar.show(errMsg);
      throw new ValidationError(errMsg, {
        path: ['buts', 0],
        key: 'BuTBerechtigungDurch',
        source: this._student.value,
        translationKey: 'errorBuTBerechtigungDurch',
      });
    }
  }

  private async _processStepAbwesenheit(student: Student) {
    if (!this._abwesenheitAgreed)
      throw new ValidationError(`Bitte bestätige!`, {
        key: '_id',
        source: student,
      });
  }

  private async _processStepFinish(student: Student) {
    await XDialog.waitFor(
      // eslint-disable-next-line complexity
      new Promise<void>(async (resolve, reject) => {
        this._student.value.joinInformation.eintrittsdatum = DateTime.now();

        if (!this._legalGuardian1AddressDifferent.value) {
          this._student.value.legalGuardian1.adresse = new Adresse({});
        }
        if (!this._legalGuardian2AddressDifferent.value) {
          this._student.value.legalGuardian2.adresse = new Adresse({});
        }

        this._student.value.consent.einverstaendnisEvaluation = true;
        this._student.value.schuelertyp = this.schoolLevel.value === 'os' ? StudentType.OS : StudentType.US;

        try {
          // Register student
          const newStudent = await Student.createStudent(this._student.value)({
            studentID: 1,
          });
          this._student.value._id = newStudent._id;
          this._student.value.studentID = newStudent.studentID;

          // Build relationship to kpf
          const rel = new StudentParticipationRelationship({
            subjectID: newStudent._id,
            predicateID: Role.STUDENT,
            objectID: this._choosenKPF.value?._id,
          });
          await SemanticRelationship.saveSemanticRelationshipsAndUpdateRoles([rel])();

          await Student.createDebitorennummer(newStudent)();

          for (const choosenDigitalCourse of this._digitalCoursesChoosen) {
            if (this._choosenKPF.value) {
              if (!choosenDigitalCourse.linkedKPFIDs) choosenDigitalCourse.linkedKPFIDs = [];
              if (!choosenDigitalCourse.linkedKPFIDs.includes(this._choosenKPF.value._id)) {
                choosenDigitalCourse.linkedKPFIDs.push(this._choosenKPF.value._id);
              }
              if (!choosenDigitalCourse.osStudentIDs.includes(this._student.value._id)) {
                choosenDigitalCourse.osStudentIDs.push(this._student.value._id);
              }
              await choosenDigitalCourse.save();
            }
          }

          await Student.confirmRegistration(this._student.value, this._choosenKPF.value?._id ?? '')();
          await Student.sendStudentRegistrationNotification(
            this._student.value,
            this._choosenKPF.value?._id ?? '',
            `${this._choosenKPF.value?.school?.name} (${this._choosenKPF.value?.kuerzel})`,
          )();

          const log1 = new RegisterEventlog({});
          log1.studentID = this._student.value._id;
          log1.type = RegisterEventlogType.PARENTS_SENDED_REGISTER_FORM;
          await log1.create();

          const log2 = new RegisterEventlog({});
          log2.studentID = this._student.value._id;
          log2.type = RegisterEventlogType.PARENTS_GOT_CONFIRMATION_MAIL;
          await log2.create();

          await AnmeldungConversionEventlog.createOrUpdateAnmeldungConversionEventlog(
            this._uniqueID,
            this.schoolLevel.value,
            this.step.value,
            true,
          )();

          resolve();
        } catch (e: any) {
          const errMsg = 'Da ist etwas schiefgegangen.\n' + e.message;

          await XDialog.alert(errMsg);
          return reject(errMsg);
        }
      }),
      'Registrierung wird abgeschlossen.',
    );
  }

  override connectedCallback() {
    super.connectedCallback();

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (A.isDevelopment && DEVELOPMENT_INSERT_MOCK_DATA) {
      this._student.value.vorname = 'Max';
      this._student.value.nachname = 'Musterman';
      this._student.value.email = `krumm-felix+${A.getGloballyUniqueID()}@adornis.de`;
      if (!this._student.value.adresse) this._student.value.adresse = new Adresse({});
      if (!this._student.value.adresse.zipCity) this._student.value.adresse.zipCity = new ZipCity({});
      this._student.value.adresse.strasse = 'Test Str. 123';
      this._student.value.staatsangehoerigkeit = 'DE';
      this._student.value.spokenLanguage = 'Deutsch';
      this._student.value.adresse.zipCity.zip = '64297';
      this._student.value.adresse.zipCity.city = 'Darmstadt';
      this._student.value.handynummer = '+49111111111';
      this._student.value.geburtstag = DateTime.now().minus({ years: 10 });

      this._student.value.legalGuardian1.vorname = 'Max';
      this._student.value.legalGuardian1.nachname = 'Mustermutter';
      this._student.value.legalGuardian1.email = this._student.value.email;
      this._student.value.legalGuardian1.handynummer = '+49111111111';
    }

    this.schoolLevel.observable.subscribe(level => {
      this._student.value.schuelertyp = level === 'os' ? StudentType.OS : StudentType.US;
      this._student.next(this._student.value);

      if (A.isDevelopment && DEVELOPMENT_INSERT_MOCK_DATA) {
        if (level === 'os') {
          this._student.value.schoolInformation.jahrgangsstufe = 10;
        }
      }
    });

    this._choosenKPF.observable
      .pipe(
        filter(Boolean),
        tap(kpf => {
          if (!kpf.produkt) return;
          const produkt = kpf.produkt;
          if (produkt instanceof ProductLKK)
            this._student.value.lernfoerderungDuerchfuehrungsTagChoosen = [...produkt.durchfuehrungsTage];
        }),
        takeUntil(this.disconnected),
      )
      .subscribe();
  }

  // eslint-disable-next-line complexity

  public override render() {
    if (this._size.value?.mobile) return this._content();

    return html` <cr-section> ${this._content()} </cr-section> `;
  }

  private _content() {
    return html`
      <cr-flex space=${this._size.mobile ? 'lg' : 'xl'}>
        <cr-flex crossaxis-center flex h100 space=${this._size.mobile ? 'lg' : 'xl'} ${css({ overflow: 'auto' })}>
          <cr-flex class="headline" crossaxis-start end>
            <cr-flex class="h-content" space="lg" padding="lg">
              <cr-h1 ${ref(this._formHeadlineEl)}>
                ${this.translation.translate(this.schoolLevel.value, 'title')}
              </cr-h1>

              ${this.step.value <= this._formularSteps.length
                ? html`
                    <cr-flex horizontal crossaxis-center space="lg" class="tablet--flex form-headline">
                      ${this.step.value >= 2
                        ? html` <cr-button @click=${() => this._nextDown()}>
                            ${this.translation.translate('back')}
                          </cr-button>`
                        : nothing}

                      <cr-h2
                        ${css({
                          color: this.colors.neutral.shade(900),
                        })}
                      >
                        ${this._formularSteps[this.step.value]?.title ?? ''}
                      </cr-h2>
                    </cr-flex>
                  `
                : nothing}
            </cr-flex>
          </cr-flex>

          ${this.step.value === 1
            ? html`<cr-h4 align="center" ${css({ padding: `0 ${this.spacing.sm}` })}>
                <b> ${this.translation.translate('step1', this.schoolLevel.value, 'sectionHeaderSchool')} </b>
              </cr-h4>`
            : nothing}

          <cr-flex class="form-content-page" space="xl" crossaxis-center h100 w100>
            ${this._formularSteps[this.step.value]?.render() ?? nothing}
            <anmeldung-component-footer
              ${css({
                flex: 'auto',
                paddingBottom: this.spacing.xl,
              })}
              .schoolLevel=${this.schoolLevel.value}
              ?show-required=${[1, 2].some(e => e === this.step.value)}
            ></anmeldung-component-footer>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _getLanguageSelection() {
    return html`<anmeldung-component-formular-lang-select
      @next-step=${() => this.step.next(1)}
      .translation=${this.translation}
    ></anmeldung-component-formular-lang-select>`;
  }

  private _getSchoolInformationSection() {
    const schools = this._filterSchools();
    return html`<anmeldung-component-formular-school-info
      .form=${this._form}
      .translation=${this.translation}
      .schoolLevel=${this.schoolLevel.value}
      .schools=${schools}
      .oldSchoolCityForFilter=${this._oldSchoolCityForFilter}
      .schoolCityForFilter=${this._schoolCityForFilter}
      .student=${this._student}
      .choosenKPF=${this._choosenKPF}
      .kpfs=${this._kpfs.value}
    >
    </anmeldung-component-formular-school-info>`;
  }

  private _getPersonalInformationSection() {
    return html`<anmeldung-component-formular-personal-info
      .form=${this._form}
      .translation=${this.translation}
      .schoolLevel=${this.schoolLevel.value}
      .student=${this._student}
      .legalGuardian1AddressDifferent=${this._legalGuardian1AddressDifferent}
      .legalGuardian2AddressDifferent=${this._legalGuardian2AddressDifferent}
    >
    </anmeldung-component-formular-personal-info>`;
  }

  private _getLKKSection() {
    return html`<anmeldung-component-formular-lkk
      .form=${this._form}
      .translation=${this.translation}
      .student=${this._student}
      .choosenKPF=${this._choosenKPF}
      .schoolLevel=${this.schoolLevel.value}
      .intensiveCourses=${this._intensiveCourses}
      .subjects=${this._subjects}
    >
    </anmeldung-component-formular-lkk>`;
  }

  private _getLKKSectionOfferHelp() {
    return html`<anmeldung-component-formular-lkk-intensive-offer-help
    .form=${this._form}
    .translation=${this.translation}
      .student=${this._student}
    >
    </anmeldung-component-formular-lkk-intensie-offer-1>`;
  }

  private _getLKKSectionOffer1() {
    return html`<anmeldung-component-formular-lkk-intensive-offer-1
      .form=${this._form}
      .translation=${this.translation}
      .student=${this._student}
      .choosenKPF=${this._choosenKPF}
      .schoolLevel=${this.schoolLevel.value}
      .intensiveCourses=${this._intensiveCourses}
      .subjects=${this._subjects}
      .digitalCoursesChoosen=${this._digitalCoursesChoosen}
      .digitalCourses=${this._digitalCourses}
    >
    </anmeldung-component-formular-lkk-intensie-offer-1>`;
  }

  private _getLKKSectionOffer2() {
    return html`<anmeldung-component-formular-lkk-intensive-offer-2
      .form=${this._form}
      .translation=${this.translation}
      .student=${this._student}
      .choosenKPF=${this._choosenKPF}
      .schoolLevel=${this.schoolLevel.value}
      .intensiveCourses=${this._intensiveCoursesSingle}
    >
    </anmeldung-component-formular-lkk-intensie-offer-2>`;
  }

  private _getLKKSectionOffer3() {
    return html`<anmeldung-component-formular-lkk-intensive-offer-3
      .form=${this._form}
      .translation=${this.translation}
      .student=${this._student}
      .choosenKPF=${this._choosenKPF}
      .schoolLevel=${this.schoolLevel.value}
      .intensiveCourses=${this._intensiveCourses}
      .subjects=${this._subjects}
    >
    </anmeldung-component-formular-lkk-intensie-offer-3>`;
  }

  private _getSupportSection() {
    return html`<anmeldung-component-formular-support
      .form=${this._form}
      .translation=${this.translation}
      .student=${this._student}
      .choosenKPF=${this._choosenKPF}
      .schoolLevel=${this.schoolLevel.value}
    >
    </anmeldung-component-formular-support>`;
  }

  private _getBUTSection() {
    return html`<anmeldung-component-formular-but
      .form=${this._form}
      .translation=${this.translation}
      .student=${this._student}
      .choosenKPF=${this._choosenKPF.value}
    >
    </anmeldung-component-formular-but>`;
  }

  private _getFinishedScreen() {
    return html`<anmeldung-component-formular-finished
      .form=${this._form}
      .translation=${this.translation}
      .schoolLevel=${this.schoolLevel.value}
    ></anmeldung-component-formular-finished> `;
  }

  private _filterSchools() {
    return (
      this._kpfs.value
        ?.sort((a, b) => a.school?.name?.toLowerCase().localeCompare(b.school?.name?.toLowerCase() || 'z') ?? -1)
        .filter(kpf => {
          if (this._schoolCityForFilter.value?.city) {
            if ((kpf.school as School).address?.zipCity?.city !== this._schoolCityForFilter.value.city) return false;
            return true;
          }
        })
        .map(p => p._id) ?? []
    );
  }

  private _abmeldungBeiAbwesenheit() {
    return html`<anmeldung-component-formular-attendance
      .form=${this._form}
      .abwesenheitAgreed=${this._abwesenheitAgreed}
      .translation=${this.translation}
      @value-picked=${e => (this._abwesenheitAgreed = e.detail.value)}
    ></anmeldung-component-formular-attendance>`;
  }

  private _getDataValidation() {
    return html`<anmeldung-component-formular-data-validation
      .form=${this._form}
      .student=${this._student.value}
      .translation=${this.translation}
      .schoolLevel=${this.schoolLevel.value}
      .digitalCoursesChoosen=${this._digitalCoursesChoosen}
      .schoolName=${this._choosenKPF.value?.school?.name}
    >
    </anmeldung-component-formular-data-validation>`;
  }

  private _getDataOverview() {
    return html`<anmeldung-component-formular-data-overview
      .form=${this._form}
      .translation=${this.translation}
      .schoolLevel=${this.schoolLevel.value}
    >
    </anmeldung-component-formular-data-overview>`;
  }

  private _nextDown() {
    this.step.next(this.step.value - 1);

    const cur = this._formularSteps[this.step.value];
    if (cur && cur.show && !cur.show()) {
      this._nextDown();
    }
  }

  private _nextUp() {
    this.step.next(this.step.value + 1);

    const cur = this._formularSteps[this.step.value];
    if (cur && cur.show && !cur.show()) {
      this._nextUp();
    }
  }
}
